import {
  brSiteLanguages,
  eeaSiteLanguages,
  frSiteLanguages,
  regularSiteLanguages,
  trSiteLanguages,
} from '@//modules/i18n/config'
import { LowerRegions } from '@/modules/configs/region'
import { getRegionalHref } from '@/modules/getRegionalHref'
import { useSpanishAccess } from '@/modules/useSpanishAccess'

import {
  company_au,
  company_br,
  company_desktop_au,
  company_desktop_br,
  company_desktop_eea,
  company_desktop_fr,
  company_desktop_global,
  company_desktop_sg,
  company_desktop_tr,
  company_desktop_us,
  company_eea,
  company_fr,
  company_global,
  company_sg,
  company_tr,
  company_us,
  CompanyLink,
  CompanySec,
  Defi,
  defi_au,
  defi_br,
  defi_eea,
  defi_fr,
  // exchange_tr,
  // exchange_us,
  defi_global,
  defi_tr,
  defi_us,
  Eco,
  eco_au,
  eco_br,
  eco_eea,
  eco_fr,
  eco_global,
  eco_us,
  Exchange,
  exchange_au,
  exchange_br,
  exchange_eea,
  exchange_global,
  Learn,
  learn_au,
  learn_br,
  learn_desktop_au,
  learn_desktop_br,
  learn_desktop_eea,
  learn_desktop_fr,
  learn_desktop_global,
  learn_desktop_tr,
  learn_desktop_us,
  learn_eea,
  learn_fr,
  learn_global,
  learn_tr,
  learn_us,
  LearnSec,
  Products,
  products_au,
  products_br,
  products_desktop_au,
  products_desktop_br,
  products_desktop_eea,
  products_desktop_fr,
  products_desktop_global,
  products_desktop_sg,
  products_desktop_tr,
  products_desktop_us,
  products_eea,
  products_fr,
  products_global,
  products_sg,
  products_tr,
  products_us,
  Section,
} from './data'

type RegionConfig = {
  products: Products[]
  isExchange: boolean
  exchange: Exchange[]
  isDefi: boolean
  defi: Defi[]
  isEco: boolean
  eco: Eco[]
  company: CompanyLink[]
  learn_desktop?: LearnSec[]
  company_desktop?: CompanySec[]
  products_desktop: Section[]
  isShowNFT: boolean
  isShowPrice: boolean
  isShowLanguages: boolean
  logoHref: string
  languages: string[]
  learn?: Learn[]
  isShowGeoIcon: boolean
}

// NOTE this function is used to generate region sites config
export const getStaticSiteConfig = (region: LowerRegions): RegionConfig => {
  const basicConfig = {
    isExchange: true,
    isDefi: true,
    isEco: true,
    isShowNFT: true,
    isShowPrice: true,
    isShowLanguages: true,
    logoHref: getRegionalHref('/', region),
    languages: regularSiteLanguages,
    isShowGeoIcon: true,
  }

  const isSpainUser = useSpanishAccess()

  if (region === 'us') {
    return {
      ...basicConfig,
      products: products_us,
      exchange: exchange_global,
      defi: defi_us,
      eco: eco_us,
      company: company_us,
      company_desktop: company_desktop_us,
      products_desktop: products_desktop_us,
      learn: learn_us,
      learn_desktop: learn_desktop_us,
      isExchange: false,
    }
  }

  if (region === 'eea') {
    //for Spanish IP user , not showing geo selection
    if (isSpainUser) {
      return {
        ...basicConfig,
        products: products_eea,
        exchange: exchange_eea,
        defi: defi_eea,
        eco: eco_eea,
        company: company_eea,
        company_desktop: company_desktop_eea,
        products_desktop: products_desktop_eea,
        // NOTE no show language switch list on EEA site
        isShowLanguages: false,
        languages: eeaSiteLanguages,
        learn: learn_eea,
        learn_desktop: learn_desktop_eea,
        isShowGeoIcon: false,
      }
    } else {
      return {
        ...basicConfig,
        products: products_eea,
        exchange: exchange_eea,
        defi: defi_eea,
        eco: eco_eea,
        company: company_eea,
        company_desktop: company_desktop_eea,
        products_desktop: products_desktop_eea,
        // NOTE no show language switch list on EEA site
        isShowLanguages: false,
        languages: eeaSiteLanguages,
        learn: learn_eea,
        learn_desktop: learn_desktop_eea,
      }
    }
  }

  if (region === 'sg') {
    return {
      ...basicConfig,
      products: products_sg,
      exchange: exchange_global,
      defi: defi_global,
      eco: eco_global,
      company: company_sg,
      company_desktop: company_desktop_sg,
      products_desktop: products_desktop_sg,

      // NOTE no these two menu on navbar on SG site
      isExchange: false,
      isDefi: false,
      isShowNFT: false,
      isShowPrice: false,
    }
  }

  if (region === 'au') {
    return {
      ...basicConfig,
      products: products_au,
      exchange: exchange_au,
      defi: defi_au,
      eco: eco_au,
      company: company_au,
      company_desktop: company_desktop_au,
      products_desktop: products_desktop_au,
      learn: learn_au,
      learn_desktop: learn_desktop_au,
    }
  }

  if (region === 'br') {
    return {
      ...basicConfig,
      products: products_br,
      exchange: exchange_br,
      defi: defi_br,
      eco: eco_br,
      company: company_br,
      company_desktop: company_desktop_br,
      products_desktop: products_desktop_br,

      // NOTE language list is different on BR site
      languages: brSiteLanguages,
      learn: learn_br,
      learn_desktop: learn_desktop_br,
    }
  }

  if (region === 'tr') {
    return {
      ...basicConfig,
      products: products_tr,
      exchange: exchange_global,
      defi: defi_tr,
      eco: eco_global,
      company: company_tr,
      company_desktop: company_desktop_tr,
      products_desktop: products_desktop_tr,

      // NOTE language list is different on TR site
      languages: trSiteLanguages,
      learn: learn_tr,
      learn_desktop: learn_desktop_tr,
      isExchange: false,
      isEco: false,
    }
  }

  if (region === 'fr') {
    return {
      ...basicConfig,
      products: products_fr,
      exchange: exchange_global,
      defi: defi_fr,
      eco: eco_fr,
      company: company_fr,
      company_desktop: company_desktop_fr,
      products_desktop: products_desktop_fr,

      // NOTE language list is different on FR site
      languages: frSiteLanguages,
      learn: learn_fr,
      learn_desktop: learn_desktop_fr,
      isExchange: false,
    }
  }

  return {
    ...basicConfig,
    products: products_global,
    exchange: exchange_global,
    defi: defi_global,
    eco: eco_global,
    company: company_global,
    company_desktop: company_desktop_global,
    learn_desktop: learn_desktop_global,
    products_desktop: products_desktop_global,
    learn: learn_global,
  }
}
